import React, { useCallback, useEffect, useState } from 'react';
import { useAccount } from '../contexts/AccountContext';
import dryftService from '../services/dryftService';
import DryftRoutesEditor from './DryftRoutesEditor';

// Detail-page monitoring panel — edit the project's run frequency + routes (config set → target).
// Both are saved together: run_frequency via PUT /dryft/{id}, routes via PUT /dryft/{id}/routes.

const DryftRoutesPanel = ({ dryftId, project, onSaved }) => {
    const { selectedAccount, selectedPartner } = useAccount();
    const [routes, setRoutes] = useState([]);
    const [configSets, setConfigSets] = useState([]);
    const [targets, setTargets] = useState([]);
    const [runFrequency, setRunFrequency] = useState(project?.run_frequency || 'daily');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [savedMsg, setSavedMsg] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        const filters = { account_id: selectedAccount || undefined, partner_id: selectedPartner || undefined };
        try {
            const [rRes, csRes, ntRes] = await Promise.all([
                dryftService.getRoutes(dryftId),
                dryftService.listConfigSets(filters),
                dryftService.listNotificationTargets(filters),
            ]);
            setRoutes((rRes.data?.routes || []).map(r => ({ config_set_id: r.config_set_id, notification_target_id: r.notification_target_id })));
            setConfigSets(csRes.data?.configSets || []);
            setTargets(ntRes.data?.targets || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load monitoring settings');
        } finally {
            setLoading(false);
        }
    }, [dryftId, selectedAccount, selectedPartner]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setRunFrequency(project?.run_frequency || 'daily'); }, [project]);

    const patchRoutes = (next) => { setSavedMsg(null); setRoutes(next); };

    const handleSave = async () => {
        if (saving) return;
        setSaving(true);
        setError(null);
        setSavedMsg(null);
        const valid = routes
            .filter(r => r.config_set_id && r.notification_target_id)
            .map(r => ({ config_set_id: r.config_set_id, notification_target_id: r.notification_target_id }));
        try {
            await dryftService.update(dryftId, { run_frequency: runFrequency });
            const res = await dryftService.setRoutes(dryftId, valid);
            setRoutes((res.data?.routes || []).map(r => ({ config_set_id: r.config_set_id, notification_target_id: r.notification_target_id })));
            setSavedMsg('Monitoring settings saved.');
            if (onSaved) onSaved();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save monitoring settings');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="pi-list-panel" style={{ marginTop: 16 }}>
                <div style={{ padding: '12px 16px', fontSize: 14, color: '#6b7280' }}>Loading monitoring settings…</div>
            </div>
        );
    }

    return (
        <div className="pi-list-panel" style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px 4px' }}>
                <span style={{ fontWeight: 600, fontSize: 14, color: '#374151' }}>Monitoring</span>
                <button className="pi-btn-add" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save monitoring'}</button>
            </div>

            {error && <div className="ppw-error-msg" style={{ margin: '4px 16px' }}>{error}</div>}
            {savedMsg && <div style={{ margin: '4px 16px', color: '#047857', fontSize: 13 }}>{savedMsg}</div>}

            <div style={{ padding: '4px 16px 14px' }}>
                <div style={{ marginBottom: 12, maxWidth: 240 }}>
                    <label className="ppw-label">Run frequency</label>
                    <select className="ppw-input" value={runFrequency} onChange={e => { setSavedMsg(null); setRunFrequency(e.target.value); }}>
                        <option value="daily">Daily</option>
                        <option value="hourly">Hourly</option>
                    </select>
                </div>
                <label className="ppw-label">Routes <span style={{ color: '#9ca3af' }}>(config set → notify)</span></label>
                <DryftRoutesEditor routes={routes} configSets={configSets} targets={targets} onChange={patchRoutes} disabled={saving} />
            </div>
        </div>
    );
};

export default DryftRoutesPanel;
